import { shouldShowConditionalField } from '../../utils/conditionalField';
import type {
  ChangeExtraFields,
  ChangeExtraValue,
  ChangeFormField,
  ChangeFormFieldType,
} from '../../types/changes';
import type { ChangeFormValues } from './changeValidation';
import { CHANGE_FIELD_LIMITS, CHANGE_MIN_TEXT_LENGTH, formSkipsAutoChecklist } from './changeValidation';
import { isEmergencyChangeType } from './changeLabels';
import { dateOnlyToPythonWeekday, localDateToPythonWeekday, weekdayName } from './goLiveWindow';

/** Emergencial não passa por comitê: Riscos/CAMUD somem do formulário e da validação. */
const EMERGENCY_HIDDEN_NATIVE_KEYS: ReadonlySet<string> = new Set([
  'risk_committee_window',
  'camud_go_no_go_date',
]);

export const isEmergencyHiddenCommitteeField = (
  nativeKey?: string | null,
  changeType?: string | null,
): boolean => {
  const key = nativeKey ?? '';
  if (!key || !isEmergencyChangeType(changeType ?? '')) return false;
  return EMERGENCY_HIDDEN_NATIVE_KEYS.has(key);
};

/** Presets legados gravam `text` / `textarea` no checklist interno. */
const LEGACY_FIELD_TYPE_MAP: Record<string, ChangeFormFieldType> = {
  text: 'text_short',
  string: 'text_short',
  short_text: 'text_short',
  textarea: 'text_long',
  long_text: 'text_long',
  multiline: 'text_long',
};

const KNOWN_CHANGE_FORM_FIELD_TYPES = new Set<ChangeFormFieldType>([
  'text_short',
  'text_long',
  'integer',
  'decimal',
  'currency',
  'date',
  'datetime',
  'period',
  'period_datetime',
  'list',
  'yes_no',
  'immediate_manager_pick',
]);

export function canonicalChangeFieldType(
  type: string | null | undefined,
): ChangeFormFieldType {
  const raw = String(type || '').trim().toLowerCase();
  const mapped = (LEGACY_FIELD_TYPE_MAP[raw] || raw) as ChangeFormFieldType;
  if (KNOWN_CHANGE_FORM_FIELD_TYPES.has(mapped)) {
    return mapped;
  }
  return 'text_short';
}

export const FORM_FIELD_TYPE_LABELS: Record<ChangeFormFieldType, string> = {
  text_short: 'Texto curto',
  text_long: 'Texto longo',
  integer: 'Número inteiro',
  decimal: 'Número decimal',
  currency: 'Moeda (R$ / € / US$)',
  date: 'Data',
  datetime: 'Data e hora',
  period: 'Período (só data)',
  period_datetime: 'Período com hora',
  list: 'Lista',
  yes_no: 'Sim/Não',
  immediate_manager_pick: 'Escolher gerente imediato',
};

export const FORM_PERIOD_MODE_LABELS: Record<string, string> = {
  range: 'Início e fim',
  start: 'Só início',
  end: 'Só fim',
  optional_range: 'Início e/ou fim',
};

export { WEEKDAY_LABELS as FORM_WEEKDAY_LABELS, weekdayName as weekdayConstraintLabel } from './goLiveWindow';

export const DATE_LIKE_FIELD_TYPES = new Set<ChangeFormFieldType>([
  'date',
  'datetime',
  'period',
  'period_datetime',
]);

export const isDateLikeFieldType = (fieldType?: string | null) =>
  Boolean(fieldType && DATE_LIKE_FIELD_TYPES.has(fieldType as ChangeFormFieldType));

export const weekdayConstraintHint = (weekday?: number | null) => {
  const label = weekdayName(weekday);
  return label ? `Deve cair numa ${label}.` : null;
};

/** Textos canônicos de ajuda para campos nativos bloqueados (espelha NATIVE_DEFAULTS no backend). */
export const NATIVE_FIELD_HELP_TEXT: Partial<Record<string, string>> = {
  change_owner_id:
    'Por padrão fica quem cria a mudança. Nas configurações deste campo dá para permitir escolher outra pessoa.',
  sap_classification:
    'Define se o redisparo de Infra vai para Hosting (SAP) ou Nuvem (Não SAP).',
  sap_has_request:
    'Se Sim, inclui aprovação extra em Transporte (cadastro Transporte SAP). Visibilidade e obrigatoriedade vêm deste formulário.',
  go_live_window: 'Início e fim com horário. O dia permitido é configurável neste cadastro.',
  risk_committee_window: 'Somente data. O dia permitido é configurável neste cadastro.',
  camud_go_no_go_date: 'Somente data. O dia permitido é configurável neste cadastro.',
};

const LEGACY_WEEKDAY_HELP_MARKERS = ['Regular:', 'Regul', 'Emergencial oculta', 'quinta-feira.', 'terça-feira.'];

const PICKER_LEAK_RE = /\s*\[["']?__picker:[^"'\]]+["']?\]\s*/g;

function helpTextLooksCorrupt(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('padraof') ||
    text.includes('__picker') ||
    text.includes('configuraçö') ||
    text.includes('\uFFFD')
  );
}

/** Ajuda exibida no formulário: corrige seed corrompido e evita duplicar dica de dia da semana. */
export function configurableFieldHelpText(field: Pick<ChangeFormField, 'kind' | 'native_key' | 'help_text' | 'weekday_constraint'>): string | null {
  let text = (field.help_text || '').replace(PICKER_LEAK_RE, '').trim();
  if (field.kind === 'native' && field.native_key && helpTextLooksCorrupt(text)) {
    text = NATIVE_FIELD_HELP_TEXT[field.native_key] || text;
  }
  if (
    field.weekday_constraint != null &&
    text &&
    LEGACY_WEEKDAY_HELP_MARKERS.some((marker) => text.includes(marker))
  ) {
    return null;
  }
  return text || null;
}

export const FORM_CONDITIONAL_OPERATORS = [
  { value: 'equals', label: 'é igual a' },
  { value: 'not_equals', label: 'é diferente de' },
  { value: 'contains', label: 'contém' },
  { value: 'greater_than', label: 'maior que' },
  { value: 'greater_equal', label: 'maior ou igual' },
  { value: 'less_than', label: 'menor que' },
  { value: 'less_equal', label: 'menor ou igual' },
  { value: 'between', label: 'está entre' },
];

export const DEFAULT_YES_NO_OPTIONS_TEXT = 'Sim\nNão';

export const CHANGE_OWNER_PICKER_LOGGED = '__picker:logged_user';
export const CHANGE_OWNER_PICKER_SELECTABLE = '__picker:selectable';

export type ChangeOwnerPickerMode = 'logged_user' | 'selectable';

export function changeOwnerPickerMode(
  field?: Pick<ChangeFormField, 'native_key' | 'options'> | null,
): ChangeOwnerPickerMode {
  if (field?.native_key !== 'change_owner_id') return 'logged_user';
  const options = field.options || [];
  if (options.includes(CHANGE_OWNER_PICKER_SELECTABLE)) return 'selectable';
  return 'logged_user';
}

export function changeOwnerAllowsSelection(
  field?: Pick<ChangeFormField, 'native_key' | 'options'> | null,
): boolean {
  return changeOwnerPickerMode(field) === 'selectable';
}

export function changeOwnerPickerOptionsFromMode(mode: ChangeOwnerPickerMode): string[] {
  return mode === 'selectable'
    ? [CHANGE_OWNER_PICKER_SELECTABLE]
    : [CHANGE_OWNER_PICKER_LOGGED];
}

export const CHANGE_OWNER_PICKER_MODE_LABELS: Record<ChangeOwnerPickerMode, string> = {
  logged_user: 'Sempre quem cria a mudança (padrão)',
  selectable: 'Permitir escolher outra pessoa na criação',
};

export function yesNoPairFromOptions(options?: string[] | null): { yesLabel: string; noLabel: string } {
  const opts = (options || []).map((item) => item.trim()).filter(Boolean);
  return {
    yesLabel: opts[0] || 'Sim',
    noLabel: opts[1] || 'Não',
  };
}

export function yesNoOptionsFromPair(yesLabel: string, noLabel: string): string[] {
  return [yesLabel.trim() || 'Sim', noLabel.trim() || 'Não'];
}

const YES_NO_SIM_TOKENS = new Set(['sim', 's', 'yes', '1', 'true']);
const YES_NO_NAO_TOKENS = new Set(['nao', 'não', 'n', 'no', '0', 'false']);

export function parseOptionsText(text: string): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const line of text.split('\n')) {
    const item = line.trim();
    if (!item || seen.has(item)) continue;
    seen.add(item);
    items.push(item);
  }
  return items;
}

export function yesNoToken(raw?: string | null): 'sim' | 'nao' | null {
  const folded = String(raw || '').trim().toLowerCase();
  if (!folded) return null;
  if (YES_NO_SIM_TOKENS.has(folded)) return 'sim';
  if (YES_NO_NAO_TOKENS.has(folded)) return 'nao';
  return null;
}

export function yesNoChoices(field: { options?: string[] | null }): Array<{ label: string; value: string }> {
  const opts = (field.options || []).map((item) => item.trim()).filter(Boolean);
  if (opts.length) return opts.map((item) => ({ label: item, value: item }));
  return [
    { label: 'Sim', value: 'sim' },
    { label: 'Não', value: 'nao' },
  ];
}

export function defaultYesNoStoredValue(
  choices: Array<{ label: string; value: string }>,
): string {
  const nao = choices.find(
    (item) => yesNoToken(item.value) === 'nao' || yesNoToken(item.label) === 'nao',
  );
  return nao?.value || choices[choices.length - 1]?.value || 'nao';
}

export function storedYesNoValue(
  field: { options?: string[] | null },
  raw?: string | null,
): string {
  const choices = yesNoChoices(field);
  const text = String(raw || '').trim();
  if (!text) return '';
  const exact = choices.find((item) => item.value === text || item.label === text);
  if (exact) return exact.value;
  const token = yesNoToken(text);
  if (token) {
    const mapped = choices.find(
      (item) => yesNoToken(item.value) === token || yesNoToken(item.label) === token,
    );
    if (mapped) return mapped.value;
  }
  const folded = text.toLowerCase();
  return (
    choices.find(
      (item) => item.value.toLowerCase() === folded || item.label.toLowerCase() === folded,
    )?.value || ''
  );
}

export function operatorsForConditionalFieldType(
  fieldType?: string | null,
): Array<{ value: string; label: string }> {
  switch (fieldType) {
    case 'text_short':
    case 'text_long':
      return FORM_CONDITIONAL_OPERATORS.filter((item) =>
        ['equals', 'not_equals', 'contains'].includes(item.value),
      );
    case 'integer':
    case 'decimal':
    case 'currency':
      return FORM_CONDITIONAL_OPERATORS.filter((item) => item.value !== 'contains');
    case 'date':
    case 'datetime':
      return [
        { value: 'equals', label: 'é igual a' },
        { value: 'not_equals', label: 'é diferente de' },
        { value: 'greater_than', label: 'é depois de' },
        { value: 'greater_equal', label: 'é depois ou igual a' },
        { value: 'less_than', label: 'é antes de' },
        { value: 'less_equal', label: 'é antes ou igual a' },
        { value: 'between', label: 'está entre' },
      ];
    case 'list':
    case 'yes_no':
      return FORM_CONDITIONAL_OPERATORS.filter((item) =>
        ['equals', 'not_equals'].includes(item.value),
      );
    default:
      return FORM_CONDITIONAL_OPERATORS.filter((item) =>
        ['equals', 'not_equals'].includes(item.value),
      );
  }
}

const emptyPeriod = (): { start: string; end: string } => ({ start: '', end: '' });
const emptyCurrency = (): { currency: string; value: string } => ({ currency: 'BRL', value: '' });

export const emptyExtraValue = (fieldType: ChangeFormFieldType): ChangeExtraValue => {
  if (fieldType === 'period' || fieldType === 'period_datetime') return emptyPeriod();
  if (fieldType === 'currency') return emptyCurrency();
  return '';
};

export const parseStoredExtraValue = (
  field: ChangeFormField,
  raw?: string | null,
): ChangeExtraValue => {
  if (raw == null || raw === '') return emptyExtraValue(field.field_type);
  if (field.field_type === 'period' || field.field_type === 'period_datetime') {
    try {
      const parsed = JSON.parse(raw) as { start?: string; end?: string };
      return { start: parsed.start || '', end: parsed.end || '' };
    } catch {
      return emptyPeriod();
    }
  }
  if (field.field_type === 'currency') {
    try {
      const parsed = JSON.parse(raw) as { currency?: string; value?: string };
      return {
        currency: (parsed.currency || 'BRL').toUpperCase(),
        value: parsed.value || '',
      };
    } catch {
      return emptyCurrency();
    }
  }
  return raw;
};

export const hydrateExtraFields = (
  fields: ChangeFormField[],
  stored?: Record<string, string | null> | null,
): ChangeExtraFields => {
  const extras: ChangeExtraFields = {};
  for (const field of fields) {
    if (field.kind !== 'extra') continue;
    extras[String(field.id)] = parseStoredExtraValue(field, stored?.[String(field.id)]);
  }
  return extras;
};

const nativeValueForCondition = (field: ChangeFormField, form: ChangeFormValues): string => {
  const key = field.native_key;
  if (!key) return '';
  if (key === 'go_live_window') return form.go_live_start_at || '';
  if (key === 'risk_committee_window') return form.risk_committee_start_at || '';
  if (key === 'task_type_ids') return (form.task_type_ids || []).length ? '1' : '';
  if (key === 'sap_has_request') {
    if (form.sap_has_request === true) return 'true';
    if (form.sap_has_request === false) return 'false';
    return '';
  }
  const value = (form as unknown as Record<string, unknown>)[key];
  if (value == null) return '';
  return String(value);
};

const extraValueForCondition = (value: ChangeExtraValue): string => {
  if (value == null) return '';
  if (typeof value === 'object') {
    if ('value' in value && 'currency' in value) return String(value.value || '');
    if ('start' in value) return value.start || '';
  }
  return String(value);
};

export const extraFieldVisibleFromNatives = (
  field: ChangeFormField,
  fields: ChangeFormField[],
  natives: Record<string, string>,
  extras: ChangeExtraFields,
): boolean => {
  if (isEmergencyHiddenCommitteeField(field.native_key, natives.change_type)) return false;
  if (!field.is_visible) return false;
  const valueMap: Record<number, string> = {};
  for (const item of fields) {
    if (item.kind === 'native') {
      const key = item.native_key || '';
      valueMap[item.id] = natives[key] || '';
    } else {
      valueMap[item.id] = extraValueForCondition(extras[String(item.id)]);
    }
  }
  return shouldShowConditionalField(field, fields, valueMap);
};

/** Visibilidade de campo nativo (condicional + cadastro), alinhado ao backend. */
export const nativeFieldVisible = (
  fields: ChangeFormField[] | null | undefined,
  nativeKey: string,
  form: ChangeFormValues,
  extras: ChangeExtraFields = {},
): boolean => {
  if (isEmergencyHiddenCommitteeField(nativeKey, form.change_type)) return false;
  if (!fields?.length) return true;
  const field = fields.find((item) => item.kind === 'native' && item.native_key === nativeKey);
  if (!field) {
    if (nativeKey === 'sap_has_request') return false;
    return true;
  }
  return extraFieldVisible(field, fields, form, extras);
};

export const extraFieldVisible = (
  field: ChangeFormField,
  fields: ChangeFormField[],
  form: ChangeFormValues,
  extras: ChangeExtraFields,
): boolean => {
  if (isEmergencyHiddenCommitteeField(field.native_key, form.change_type)) return false;
  const natives: Record<string, string> = {};
  for (const item of fields) {
    if (item.kind === 'native') {
      natives[item.native_key || ''] = nativeValueForCondition(item, form);
    }
  }
  return extraFieldVisibleFromNatives(field, fields, natives, extras);
};

export const validateNativeRequired = (
  fields: ChangeFormField[],
  form: ChangeFormValues,
  extras: ChangeExtraFields = {},
): string | null => {
  const skipChecklist = formSkipsAutoChecklist(fields, form, extras);
  for (const field of fields) {
    if (field.kind !== 'native' || !field.is_required) continue;
    if (!extraFieldVisible(field, fields, form, extras)) continue;
    const key = field.native_key || '';
    if (skipChecklist && key === 'risk_committee_window') continue;
    const value = nativeValueForCondition(field, form);
    if (!String(value || '').trim()) {
      return `Preencha o campo obrigatório: ${field.label}.`;
    }
  }
  return null;
};

export const extraValueIsBlank = (
  value: ChangeExtraValue,
  field?: ChangeFormField,
): boolean => {
  if (value == null) return true;
  if (typeof value !== 'object') return !String(value).trim();
  if ('currency' in value) {
    return !String((value as { value?: string }).value || '').trim();
  }
  const period = value as { start?: string; end?: string };
  const start = (period.start || '').trim();
  const end = (period.end || '').trim();
  const mode = field?.period_mode || 'range';
  if (mode === 'start') return !start;
  if (mode === 'end') return !end;
  if (mode === 'optional_range') return !start && !end;
  return !start || !end;
};

const extraPartWeekdayError = (
  field: ChangeFormField,
  value: string,
  dateOnly: boolean,
): string | null => {
  const weekday = field.weekday_constraint;
  if (weekday == null || !value.trim()) return null;
  const actual = dateOnly ? dateOnlyToPythonWeekday(value) : localDateToPythonWeekday(value);
  if (actual === weekday) return null;
  const label = weekdayName(weekday);
  return label ? `${field.label} deve cair numa ${label}.` : null;
};

export const extraWeekdayError = (
  field: ChangeFormField,
  value: ChangeExtraValue,
): string | null => {
  if (field.weekday_constraint == null) return null;
  const dateOnly = field.field_type === 'date' || field.field_type === 'period';
  if (typeof value === 'string') {
    return extraPartWeekdayError(field, value, dateOnly);
  }
  if (typeof value === 'object' && value && 'start' in value) {
    return (
      extraPartWeekdayError(field, value.start || '', dateOnly) ||
      extraPartWeekdayError(field, value.end || '', dateOnly)
    );
  }
  return null;
};

export const validateExtraFieldsFromNatives = (
  fields: ChangeFormField[],
  natives: Record<string, string>,
  extras: ChangeExtraFields,
): string | null => {
  for (const field of fields) {
    if (field.kind !== 'extra') continue;
    if (!extraFieldVisibleFromNatives(field, fields, natives, extras)) continue;
    const fieldType = canonicalChangeFieldType(field.field_type);
    const value = extras[String(field.id)];
    if (field.is_required && extraValueIsBlank(value, field)) {
      return `Preencha o campo obrigatório: ${field.label}.`;
    }
    if (extraValueIsBlank(value, field) || value == null) continue;
    if (typeof value === 'string') {
      if (value.includes('\u0000')) return `${field.label}: valor inválido.`;
      if (fieldType === 'text_short' && value.length > CHANGE_FIELD_LIMITS.title) {
        return `${field.label}: no máximo ${CHANGE_FIELD_LIMITS.title} caracteres.`;
      }
      if (fieldType === 'text_long' && value.length > CHANGE_FIELD_LIMITS.longText) {
        return `${field.label}: texto longo demais.`;
      }
      if (fieldType === 'integer' && !/^-?\d+$/.test(value.trim())) {
        return `${field.label}: informe um número inteiro.`;
      }
      if (fieldType === 'decimal' && !/^-?\d+([.,]\d+)?$/.test(value.trim())) {
        return `${field.label}: informe um número.`;
      }
      if (
        (fieldType === 'text_short' || fieldType === 'text_long') &&
        value.trim().length < CHANGE_MIN_TEXT_LENGTH &&
        field.is_required
      ) {
        return `${field.label}: deve ter ao menos ${CHANGE_MIN_TEXT_LENGTH} caracteres.`;
      }
    }
    if (typeof value === 'object' && value && 'currency' in value && 'value' in value) {
      const currency = String(value.currency || '').toUpperCase();
      const amount = String(value.value || '').trim().replace(',', '.');
      if (!['BRL', 'EUR', 'USD'].includes(currency)) {
        return `${field.label}: moeda inválida. Use BRL, EUR ou USD.`;
      }
      if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
        return `${field.label}: informe um valor monetário válido.`;
      }
    }
    if (
      typeof value === 'object' &&
      value &&
      'start' in value &&
      value.end &&
      value.start &&
      value.end < value.start
    ) {
      return `${field.label}: o fim precisa ser maior ou igual ao início.`;
    }
    const weekdayError = extraWeekdayError(field, value);
    if (weekdayError) return weekdayError;
  }
  return null;
};

export const validateExtraFields = (
  fields: ChangeFormField[],
  form: ChangeFormValues,
  extras: ChangeExtraFields,
): string | null => {
  const natives: Record<string, string> = {};
  for (const item of fields) {
    if (item.kind === 'native') {
      natives[item.native_key || ''] = nativeValueForCondition(item, form);
    }
  }
  return validateExtraFieldsFromNatives(fields, natives, extras);
};

export const buildExtraFieldsPayloadFromNatives = (
  fields: ChangeFormField[],
  natives: Record<string, string>,
  extras: ChangeExtraFields,
): Record<string, ChangeExtraValue> => {
  const payload: Record<string, ChangeExtraValue> = {};
  for (const field of fields) {
    if (field.kind !== 'extra') continue;
    if (!extraFieldVisibleFromNatives(field, fields, natives, extras)) continue;
    const value = extras[String(field.id)];
    if (extraValueIsBlank(value, field)) continue;
    payload[String(field.id)] = value;
  }
  return payload;
};

export const buildExtraFieldsPayload = (
  fields: ChangeFormField[],
  form: ChangeFormValues,
  extras: ChangeExtraFields,
): Record<string, ChangeExtraValue> => {
  const natives: Record<string, string> = {};
  for (const item of fields) {
    if (item.kind === 'native') {
      natives[item.native_key || ''] = nativeValueForCondition(item, form);
    }
  }
  return buildExtraFieldsPayloadFromNatives(fields, natives, extras);
};
